import React, { useEffect, useState, useRef } from "react";
import { useGetNodeRunInstancesForRunInstance, useGetRunInstancesForRun, useGetRunById, useRetryRun } from "../../api/workflowsApi";
import { NodeRunInstance, Run, RunInstance } from "../../types/workflow";
import { Close } from "@mui/icons-material";
import StatusChip from "../Common/StatusChip";
import { RunState, RunInstanceStateToName, RunStateToColor, RunStateToName } from "../../constants/workflow";
import { LightningBoltOutline, VerticalLine } from "../../svgUrls";
import WorkflowHistoryNode from "./WorkflowHistoryNode";
import MenuRadioDropdown from "../Common/MenuRadioDropdown";
import { formatDateTime } from "../../utils/misc";
import { 
    useAddOneShotSampleFromNodeRun, 
    useRemoveOneShotSampleFromNodeRun, 
    useCheckOneShotSampleExists 
} from "../../api/oneShotSampleApi";
import { Checkbox, FormControlLabel, Tooltip, CircularProgress } from "@mui/material";
import clsx from "clsx";
import queryClient from "../../queryClient";
import { queryKeys } from "../../queryKeys";

type WorkflowHistoryDetailsProps = {
    selectedRun: Run;
    closeDetails: () => void;
}

const WorkflowHistoryDetails = ({
    selectedRun,
    closeDetails,
}: WorkflowHistoryDetailsProps) => {
    // Fetch the latest run data to get updated status
    const { data: latestRun, refetch: refetchRun } = useGetRunById(selectedRun.id);
    const { data: runInstances, refetch: refetchRunInstances } = useGetRunInstancesForRun(selectedRun.id);
    const { mutate: getNodeRunInstances } = useGetNodeRunInstancesForRunInstance();
    const [selectedRunInstance, setSelectedRunInstance] = useState<RunInstance | null>(null);
    const [nodeRunInstances, setNodeRunInstances] = useState<NodeRunInstance[]>([]);
    const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const { mutate: retryRun, isPending: isRetrying } = useRetryRun();
    
    // Use the latest run data if available, otherwise fallback to selectedRun prop
    const currentRun = latestRun || selectedRun;
    const workflowId = currentRun.workflow?.id;
    
    // Track previous run state to detect status changes (initialize with selectedRun state)
    const previousRunStateRef = useRef<RunState | null>(selectedRun.state || null);
    
    useEffect(() => {
        if(runInstances?.length && Array.isArray(runInstances)) {
            // Backend returns instances in ASC order (oldest first), so last one is newest
            const newestInstance = runInstances[runInstances.length - 1];
            
            // If we don't have a selection yet, or the selected one doesn't exist anymore, select the newest
            if (!selectedRunInstance || !runInstances.find(inst => inst.id === selectedRunInstance.id)) {
                setSelectedRunInstance(newestInstance);
            }
            // If a new instance was added and it's different from what we're viewing, switch to it
            else if (newestInstance.id !== selectedRunInstance.id && 
                     new Date(newestInstance.created_time) > new Date(selectedRunInstance.created_time)) {
                setSelectedRunInstance(newestInstance);
            }
        }
    }, [runInstances?.length, runInstances]);

    useEffect(() => {
        if(selectedRunInstance?.id) {
            getNodeRunInstances({ runInstanceId: selectedRunInstance.id }, {
                onSuccess: (data) => {
                    setNodeRunInstances(data);
                }
            })
        }
    }, [getNodeRunInstances, selectedRunInstance]);

    // Track status changes and refresh parent run history page when status changes to completed or failed
    useEffect(() => {
        const previousState = previousRunStateRef.current;
        const currentState = currentRun.state;
        
        // If status changed from pending to completed or failed, refresh the parent run history page
        if (
            previousState !== null &&
            previousState === RunState.RunPending && 
            (currentState === RunState.RunCompleted || currentState === RunState.RunFailed) &&
            workflowId
        ) {
            // Invalidate all workflow runs queries (for all pages, limits, and states)
            queryClient.invalidateQueries({ 
                queryKey: queryKeys.workflowRuns(workflowId),
                exact: false 
            });
            // Also invalidate the runs count query
            queryClient.invalidateQueries({ 
                queryKey: queryKeys.workflowRunsCount(workflowId),
                exact: false 
            });
        }
        
        // Update the previous state reference
        if (currentState !== undefined) {
            previousRunStateRef.current = currentState;
        }
    }, [currentRun.state, workflowId]);

    // Auto-refresh data every 15 seconds when popover is open
    useEffect(() => {
        const refreshData = () => {
            // Refetch run data to get updated status
            refetchRun();
            // Refetch run instances
            refetchRunInstances();
            
            // Refetch node run instances if we have a selected run instance
            if(selectedRunInstance?.id) {
                getNodeRunInstances({ runInstanceId: selectedRunInstance.id }, {
                    onSuccess: (data) => {
                        setNodeRunInstances(data);
                    }
                });
            }
        };

        // Set up interval to refresh every 15 seconds (15000ms)
        refreshIntervalRef.current = setInterval(refreshData, 15000);

        // Cleanup interval on unmount
        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        };
    }, [refetchRun, refetchRunInstances, getNodeRunInstances, selectedRunInstance?.id]);

    return (
        <div className="h-full flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-borderGrey bg-white sticky top-0 z-10">
                <div>
                    <div className="font-semibold text-greyishBlueMid mb-1">Run Details</div>
                    <div className="text-xs text-greyishBlue font-mono">ID: {currentRun.id}</div>
                </div>
                <button
                    onClick={closeDetails}
                    className="p-2 hover:bg-backgroundGrey rounded-lg transition-colors cursor-pointer"
                >
                    <Close className="text-greyishBlue" />
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto px-6 py-4">
                <div className="flex gap-2 items-center mb-4 pb-4 border-b border-borderGrey">
                    <StatusChip
                        statusText={RunStateToName[currentRun.state]}
                        themeColor={RunStateToColor[currentRun.state]}
                        showBorder
                        additionalClasses="font-medium"
                    />
                    {
                        runInstances?.length && Array.isArray(runInstances) ?
                            <MenuRadioDropdown
                                menuOptions={
                                    runInstances.map((instance, index) => ({id: instance.id, value: `Instance #${index+1}`, valueDetail: instance.id}))
                                }
                                selectedOption={selectedRunInstance?.id || ''}
                                setSelectedOption={(selectedId) => {
                                    const selectedInstance = runInstances?.find(instance => instance.id === selectedId);
                                    if(selectedInstance) {
                                        setSelectedRunInstance(selectedInstance);
                                    }
                                }}
                            />
                            : null
                    }
                    {currentRun.created_time ?
                        <div className="text-greyishBlue text-xs ml-auto">{formatDateTime(currentRun.created_time)}</div>
                        : null
                    }
                </div>
            
                {
                    nodeRunInstances?.length && Array.isArray(nodeRunInstances) ?
                        <div className="flex flex-col items-center">
                            <div className="flex gap-2 items-center p-2 bg-backgroundGrey text-greyishBlue text-xs rounded-lg mb-2">
                                <LightningBoltOutline /> 
                                Run triggered on {selectedRunInstance ? formatDateTime(selectedRunInstance.updated_time) : ''}
                            </div>
                            <VerticalLine />
                            {
                                nodeRunInstances.sort((a, b) => {
                                    const createdTimeA = new Date(a.created_time).getTime();
                                    const createdTimeB = new Date(b.created_time).getTime();
                                    return createdTimeA - createdTimeB
                                 }).map((element, index) => {
                                    return (
                                        <React.Fragment key={element.id}>
                                            <WorkflowHistoryNode
                                                element={element}
                                                elementIndex={index}
                                            />
                                        </React.Fragment>
                                    )
                                })
                            }
                            <div className={clsx(
                                "p-2 text-xs rounded-lg mt-2 flex items-center gap-2 justify-center",
                                currentRun.state === RunState.RunPending 
                                    ? "bg-[#fff5e9] text-[#DF8518]"
                                    : currentRun.state === RunState.RunCompleted
                                    ? "bg-[#ecf6ee] text-[#18855E]"
                                    : currentRun.state === RunState.RunFailed
                                    ? "bg-[#f9e5e5] text-[#D02B2B]"
                                    : "bg-backgroundGrey text-greyishBlue"
                            )}>
                                {currentRun.state === RunState.RunPending ? (
                                    <>
                                        <CircularProgress size={12} thickness={4} className="!text-[#DF8518]" />
                                        <span>Workflow Running</span>
                                    </>
                                ) : (
                                    <span>Workflow {RunStateToName[currentRun.state] || 'Unknown'}</span>
                                )}
                            </div>
                        </div>
                        : null
                }
            </div>

            {currentRun.state === RunState.RunFailed && (
                <div className="px-6 py-4 bg-white sticky bottom-0 z-10">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            className="flex-1 px-3 py-2 text-xs rounded-lg border border-borderGrey text-greyishBlueMid hover:bg-backgroundBlue hover:border-nanonetsBlue/30 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={isRetrying}
                            onClick={() => retryRun({ runId: currentRun.id, mode: 'whole' })}
                        >
                            Retry whole run
                        </button>
                        <button
                            className="flex-1 px-3 py-2 text-xs rounded-lg border border-borderGrey text-greyishBlueMid hover:bg-backgroundBlue hover:border-nanonetsBlue/30 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={isRetrying}
                            onClick={() => retryRun({ runId: currentRun.id, mode: 'failed_nodes' })}
                        >
                            Retry failed nodes
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default WorkflowHistoryDetails;